import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DispatchStatusBadge } from '@/shared/components/DispatchStatusBadge';
import { useQuery } from '@tanstack/react-query';
import { AccountsSalesInvoice } from '../types';

/** Exactly what POST /api/files/upload returns, and what the payment stores. */
interface PaymentAttachment {
  fileUrl: string;
  originalName: string;
  mimeType: string;
  size: number;
}
import { InvoiceGenerator } from './InvoiceGenerator';
import { InvoiceDetail } from './InvoiceDetail';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { Modal } from '@/shared/components/ui/Modal';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { SearchableSelect } from '@/shared/components/ui/SearchableSelect';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useRememberedState } from '@/shared/hooks/useRememberedFilters';
import { PlusCircle, FileText, CreditCard, Search } from 'lucide-react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { api, getApiErrorMessage, getApiErrorStatus } from '@/shared/api/axios';
import { formatINR } from '@/shared/utils/currency';
import { Badge } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import toast from 'react-hot-toast';
import { localToday } from '@/shared/utils/utils';

type View = 'list' | 'generate' | 'detail';

/**
 * Every invoice, not just the newest page — the filters below (same set as the
 * Sales Invoices page: search, sort, employee, client, product, dates) work on
 * the whole list. Under ['accounts', 'sales-invoices'] so every existing
 * invalidation refreshes it too.
 */
function useAllSalesInvoices() {
  return useQuery({
    queryKey: ['accounts', 'sales-invoices', 'all'],
    queryFn: async () => {
      const all: AccountsSalesInvoice[] = [];
      for (let page = 1; page <= 50; page++) {
        const { data } = await api.get('/api/accounts/sales-invoices', { params: { page, limit: 200 } });
        const rows: AccountsSalesInvoice[] = Array.isArray(data) ? data : (data.data || []);
        all.push(...rows);
        const total = Array.isArray(data) ? rows.length : Number(data.pagination?.total ?? rows.length);
        if (rows.length < 200 || all.length >= total) break;
      }
      return all;
    },
  });
}

type InvoiceSort = 'date_desc' | 'date_asc' | 'invoice_desc' | 'invoice_asc' | 'customer_asc' | 'customer_desc' | 'amount_desc' | 'amount_asc';

const SORT_OPTIONS: { value: InvoiceSort; label: string }[] = [
  { value: 'date_desc', label: 'Newest first' },
  { value: 'date_asc', label: 'Oldest first' },
  { value: 'invoice_desc', label: 'Invoice no. (latest first)' },
  { value: 'invoice_asc', label: 'Invoice no. (oldest first)' },
  { value: 'customer_asc', label: 'Client (A-Z)' },
  { value: 'customer_desc', label: 'Client (Z-A)' },
  { value: 'amount_desc', label: 'Amount (high-low)' },
  { value: 'amount_asc', label: 'Amount (low-high)' },
];

const customerName = (inv: AccountsSalesInvoice) => String((inv.customerId as any)?.name || '');
const amountOf = (inv: AccountsSalesInvoice) => Number(inv.netTotal || inv.grandTotal) || 0;
/** RBM/26-27/2118 → [26, 2118] (financial year, then number); drafts (no number) → null, sorted last. */
const invoiceKey = (inv: AccountsSalesInvoice): [number, number] | null => {
  const no = String(inv.invoiceNumber || '').trim();
  const fy = no.match(/(\d{2})-\d{2}\/(\d+)\s*$/);
  if (fy) return [Number(fy[1]), Number(fy[2])];
  const tail = no.match(/(\d+)\s*$/);
  return tail ? [0, Number(tail[1])] : null;
};
const repIdOf = (inv: any) => String(inv.originatingRepId?._id || inv.originatingRepId || inv.createdBy?._id || inv.createdBy || '');

export function InvoiceList() {
  // Every filter here stays set until it is reset (client, 2026-10-03) —
  // opening an invoice, another menu, a refresh: it is still there.
  const [statusFilter, setStatusFilter] = useRememberedState('accounts-invoices', 'status', '');
  const { data: invoices, isLoading, error } = useAllSalesInvoices();

  // Same filters as the Sales Invoices page.
  const [searchInput, setSearchInput] = useRememberedState('accounts-invoices', 'search', '');
  // Numbering-wise by default (client, 2026-10-03); drafts with no number come last.
  const [sort, setSort] = useRememberedState<InvoiceSort>('accounts-invoices', 'sort', 'invoice_desc');
  const [repFilter, setRepFilter] = useRememberedState('accounts-invoices', 'employee', '');
  const [clientFilter, setClientFilter] = useRememberedState('accounts-invoices', 'client', '');
  const [productFilter, setProductFilter] = useRememberedState('accounts-invoices', 'product', '');
  // Invoice-number range (numbering-wise): "2080" or "RBM/26-27/2080" both work.
  const [noFrom, setNoFrom] = useRememberedState('accounts-invoices', 'noFrom', '');
  const [noTo, setNoTo] = useRememberedState('accounts-invoices', 'noTo', '');
  const [fromDate, setFromDate] = useRememberedState('accounts-invoices', 'fromDate', '');
  const [toDate, setToDate] = useRememberedState('accounts-invoices', 'toDate', '');
  const [datePreset, setDatePreset] = useRememberedState('accounts-invoices', 'datePreset', '');
  const applyPreset = (preset: string) => {
    setDatePreset(preset);
    const now = new Date();
    const d = (y: number, m: number, day: number) => localToday(new Date(y, m, day));
    const y = now.getFullYear(), m = now.getMonth();
    const fyStart = m >= 3 ? y : y - 1;
    const ranges: Record<string, [string, string]> = {
      today: [localToday(now), localToday(now)],
      yesterday: [d(y, m, now.getDate() - 1), d(y, m, now.getDate() - 1)],
      this_week: [d(y, m, now.getDate() - ((now.getDay() + 6) % 7)), localToday(now)],
      this_month: [d(y, m, 1), localToday(now)],
      last_month: [d(y, m - 1, 1), d(y, m, 0)],
      this_fy: [d(fyStart, 3, 1), localToday(now)],
    };
    const r = ranges[preset];
    setFromDate(r ? r[0] : '');
    setToDate(r ? r[1] : '');
  };
  useEffect(() => {
    if (datePreset && datePreset !== 'custom') applyPreset(datePreset);
    // once, on open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data: userOptions = [] } = useUserOptions(true);

  const list = invoices || [];
  const clientOptions = useMemo(
    () => [...new Set(list.map(customerName).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [list],
  );
  const productOptions = useMemo(
    () => [...new Set(list.flatMap((inv: any) => (inv.lineItems || []).map((l: any) => String(l.description || '').trim())).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b)),
    [list],
  );
  // Only people who actually have invoices here — the BDM who owns the sale
  // (originatingRepId), else whoever created it.
  const repOptions = useMemo(() => {
    const names = new Map(userOptions.map((u) => [String(u.id), u.display_name]));
    const out = new Map<string, string>();
    for (const inv of list as any[]) {
      const id = repIdOf(inv);
      if (!id || out.has(id)) continue;
      out.set(id, names.get(id) || inv.createdBy?.display_name || 'Unknown');
    }
    return [...out].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [list, userOptions]);

  const numberOf = (v: string) => { const m = String(v || '').trim().match(/(\d+)\s*$/); return m ? Number(m[1]) : null; };
  const noFromN = numberOf(noFrom);
  const noToN = numberOf(noTo);

  function applyFilters(rows: AccountsSalesInvoice[]) {
    const q = searchInput.trim().toLowerCase();
    const out = rows.filter((inv: any) => {
      if (statusFilter && inv.status !== statusFilter) return false;
      if (q) {
        const hay = [inv.invoiceNumber, customerName(inv), inv.customerId?.gstNumber, inv.customerId?.phone, inv.customerId?.email]
          .map((v) => String(v || '').toLowerCase()).join(' ');
        if (!hay.includes(q)) return false;
      }
      if (repFilter && repIdOf(inv) !== repFilter) return false;
      if (noFromN !== null || noToN !== null) {
        const k = invoiceKey(inv);
        if (!k) return false;
        if (noFromN !== null && k[1] < noFromN) return false;
        if (noToN !== null && k[1] > noToN) return false;
      }
      if (clientFilter && customerName(inv) !== clientFilter) return false;
      if (productFilter && !(inv.lineItems || []).some((l: any) => String(l.description || '').trim() === productFilter)) return false;
      if (fromDate || toDate) {
        const day = inv.date ? localToday(new Date(inv.date)) : '';
        if (!day || (fromDate && day < fromDate) || (toDate && day > toDate)) return false;
      }
      return true;
    });
    const byDate = (inv: AccountsSalesInvoice) => new Date(inv.date || 0).getTime();
    const cmpNo = (a: AccountsSalesInvoice, b: AccountsSalesInvoice, dir: 1 | -1) => {
      const ka = invoiceKey(a), kb = invoiceKey(b);
      if (!ka && !kb) return byDate(b) - byDate(a);
      if (!ka) return 1;
      if (!kb) return -1;
      return dir * ((ka[0] - kb[0]) || (ka[1] - kb[1]));
    };
    const sorters: Record<InvoiceSort, (a: AccountsSalesInvoice, b: AccountsSalesInvoice) => number> = {
      date_desc: (a, b) => byDate(b) - byDate(a),
      date_asc: (a, b) => byDate(a) - byDate(b),
      invoice_desc: (a, b) => cmpNo(a, b, -1),
      invoice_asc: (a, b) => cmpNo(a, b, 1),
      customer_asc: (a, b) => customerName(a).localeCompare(customerName(b)),
      customer_desc: (a, b) => customerName(b).localeCompare(customerName(a)),
      amount_desc: (a, b) => amountOf(b) - amountOf(a),
      amount_asc: (a, b) => amountOf(a) - amountOf(b),
    };
    return [...out].sort(sorters[sort]);
  }
  const qc = useQueryClient();
  const [view, setView] = useState<View>('list');
  const [selectedInvoice, setSelectedInvoice] = useState<AccountsSalesInvoice | null>(null);
  const [prefillData, setPrefillData] = useState<any>(null);

  // Cross-Entity Search links an invoice result here with ?invoiceId= (the
  // search results themselves used to be plain, unclickable text) —
  // consumed once the invoice is found in the loaded list, then stripped
  // from the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const invoiceId = searchParams.get('invoiceId');
    if (!invoiceId || !invoices || invoices.length === 0) return;
    const found = invoices.find((inv: AccountsSalesInvoice) => inv._id === invoiceId);
    if (found) {
      setSelectedInvoice(found);
      setView('detail');
      const next = new URLSearchParams(searchParams);
      next.delete('invoiceId');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, invoices, setSearchParams]);

  // Payment Recording Modal State
  const [payModalInvoice, setPayModalInvoice] = useState<AccountsSalesInvoice | null>(null);
  const [payAmount, setPayAmount] = useState<number>(0);
  const [payMode, setPayMode] = useState<string>('bank_transfer');
  const [payRef, setPayRef] = useState<string>('');
  const [payDate, setPayDate] = useState<string>(localToday());
  /** Proof of payment the customer sent — receipt, screenshot, bank advice. */
  const [payFiles, setPayFiles] = useState<PaymentAttachment[]>([]);
  const [uploading, setUploading] = useState(false);

  const recordPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!payModalInvoice) return;
      const custId = typeof payModalInvoice.customerId === 'string'
        ? payModalInvoice.customerId
        : (payModalInvoice.customerId as any)?._id;

      const totalAmount = Number(payAmount);
      const invoiceDue = Number(payModalInvoice.amountDue || totalAmount);
      const allocatedAmount = Math.max(0, Math.min(totalAmount, invoiceDue));

      return api.post('/api/accounts/payments', {
        type: 'receipt',
        date: payDate,
        partyType: 'customer',
        partyId: custId,
        amount: totalAmount,
        paymentMethod: payMode,
        referenceNumber: payRef,
        attachments: payFiles,
        allocations: allocatedAmount > 0 ? [
          {
            documentType: 'invoice',
            documentId: payModalInvoice._id,
            amount: allocatedAmount,
          },
        ] : [],
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounts'] });
      toast.success(
        payAmount > (payModalInvoice?.amountDue || 0)
          ? 'Payment recorded: Invoice settled & surplus credited to Customer Advance'
          : 'Payment recorded successfully'
      );
      setPayFiles([]);
      setPayModalInvoice(null);
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to record payment')),
  });

  const handleOpenPayModal = (inv: AccountsSalesInvoice, e: React.MouseEvent) => {
    e.stopPropagation();
    setPayModalInvoice(inv);
    setPayAmount(inv.amountDue || 0);
    setPayMode('bank_transfer');
    setPayRef('');
    setPayDate(localToday());
  };

  const handleViewInvoice = async (inv: AccountsSalesInvoice) => {
    try {
      const { data } = await api.get(`/api/accounts/sales-invoices/${inv._id}`);
      setSelectedInvoice(data);
      setView('detail');
    } catch { setSelectedInvoice(inv); setView('detail'); }
  };

  const handleGenerate = () => { setPrefillData(null); setView('generate'); };
  const handleGenerateSuccess = () => { qc.invalidateQueries({ queryKey: ['accounts', 'sales-invoices'] }); setView('list'); };
  const handleDuplicate = (data: any) => { setPrefillData(data); setView('generate'); };
  const handleEdit = (data: any) => { setPrefillData(data); setView('generate'); };

  if (view === 'generate') return <InvoiceGenerator onSuccess={handleGenerateSuccess} onCancel={() => setView('list')} prefillData={prefillData} />;
  if (view === 'detail' && selectedInvoice) return <InvoiceDetail invoice={selectedInvoice} onBack={() => { setSelectedInvoice(null); setView('list'); }} onDuplicate={handleDuplicate} onEdit={handleEdit} />;
  if (isLoading) return (
    <div className="space-y-3" aria-busy="true" aria-live="polite">
      <Skeleton className="h-16 w-full rounded-card" />
      <Skeleton className="h-10 w-full rounded-card-sm" />
      <Skeleton className="h-[340px] w-full rounded-card" />
    </div>
  );

  // A refused or failed read must never render as "no invoices yet" — that reads
  // as a confirmed zero balance to whoever is chasing money.
  if (error) {
    const denied = getApiErrorStatus(error) === 403;
    return (
      <div role="alert" className={`rounded-card border p-4 text-sm ${denied ? 'border-warning/25 bg-warning/10 text-warning-text' : 'border-danger/20 bg-danger/10 text-danger-text'}`}>
        <p className="font-semibold">{denied ? 'Permission denied' : 'Could not load sales invoices'}</p>
        <p className="mt-1 text-body-sm">
          {denied
            ? 'Your role does not include the sales invoice ledger. Ask an Accounts administrator for read access.'
            : getApiErrorMessage(error, 'The invoice ledger did not respond. Retry from the Accounts page.')}
        </p>
      </div>
    );
  }

  // Status chips are read as text, so they take the `-text` pair, not the fills.
  const statusBadge = (status: string) => {
    const tones: Record<string, 'success' | 'warning' | 'danger' | 'neutral' | 'primary' | 'info'> = {
      paid: 'success',
      partially_paid: 'warning',
      unpaid: 'danger',
      cancelled: 'neutral',
      reversed: 'danger',
      draft: 'neutral',
    };
    return <Badge tone={tones[status] ?? 'neutral'}>{status?.replace('_', ' ')}</Badge>;
  };

  const renderDispatchStatus = (ds?: AccountsSalesInvoice['dispatchStatus']) => <DispatchStatusBadge status={ds} />;

  const allInvoices = invoices || [];
  const filtered = applyFilters(allInvoices);
  const totalValue = filtered.reduce((s, inv) => s + amountOf(inv), 0);
  const totalDue = filtered.reduce((s, inv) => s + (Number(inv.amountDue) || 0), 0);
  const anyFilter = !!(searchInput.trim() || repFilter || clientFilter || productFilter || fromDate || toDate || statusFilter || noFrom.trim() || noTo.trim());

  return (
    <Card className="shadow-xs h-full flex flex-col overflow-hidden">
      <CardHeader title="Sales Invoices" subtitle="Invoices raised here, receipts recorded against them, and what is still outstanding." action={
        <Button size="sm" onClick={handleGenerate}>
          <PlusCircle className="h-4 w-4 mr-1" /> New Invoice
        </Button>
      } />
      <div className="px-4 py-2.5 border-b border-border flex gap-2 flex-wrap">
        {['', 'unpaid', 'partially_paid', 'paid', 'draft', 'cancelled'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} aria-pressed={statusFilter === s} className={`h-8 px-3 text-xs font-medium rounded-lg border transition ${statusFilter === s ? 'bg-primary-container text-on-primary-container border-primary/25 font-semibold' : 'bg-background border-border text-muted-foreground hover:text-foreground'}`}>
            {s === '' ? 'All' : s === 'partially_paid' ? 'Partial' : s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>
      {/* Same filters as the Sales Invoices page. */}
      <div className="px-4 py-2.5 border-b flex gap-2 flex-wrap items-center">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search number, client, GSTIN, phone..."
            className="h-10 w-full rounded-lg border border-border bg-background pl-8 pr-3 text-xs"
          />
        </div>
        <div className="w-48">
          <Select name="acc-invoices-sort" value={sort} onChange={(e) => setSort(e.target.value as InvoiceSort)} options={SORT_OPTIONS} />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          Invoice no.
          <input type="text" inputMode="numeric" aria-label="Invoice number from" value={noFrom}
            onChange={(e) => setNoFrom(e.target.value)} placeholder="from"
            className="h-10 w-20 rounded-lg border border-border bg-background px-2 text-xs" />
          –
          <input type="text" inputMode="numeric" aria-label="Invoice number to" value={noTo}
            onChange={(e) => setNoTo(e.target.value)} placeholder="to"
            className="h-10 w-20 rounded-lg border border-border bg-background px-2 text-xs" />
        </label>
        <div className="w-44">
          <Select
            name="acc-invoices-employee"
            value={repFilter}
            onChange={(e) => setRepFilter(e.target.value)}
            options={[{ value: '', label: 'All employees' }, ...repOptions]}
          />
        </div>
        <div className="w-56">
          <SearchableSelect
            name="acc-invoices-client"
            value={clientFilter}
            onChange={setClientFilter}
            placeholder="All clients"
            searchPlaceholder="Search client…"
            emptyText="No client found"
            clearable
            options={clientOptions.map((c) => ({ value: c, label: c }))}
          />
        </div>
        <div className="w-56">
          <SearchableSelect
            name="acc-invoices-product"
            value={productFilter}
            onChange={setProductFilter}
            placeholder="All products"
            searchPlaceholder="Search product…"
            emptyText="No product found"
            clearable
            options={productOptions.map((p) => ({ value: p, label: p }))}
          />
        </div>
        <div className="w-40">
          <Select
            name="acc-invoices-date-preset"
            value={datePreset}
            onChange={(e) => applyPreset(e.target.value)}
            options={[
              { value: '', label: 'All dates' },
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'this_week', label: 'This week' },
              { value: 'this_month', label: 'This month' },
              { value: 'last_month', label: 'Last month' },
              { value: 'this_fy', label: 'This financial year' },
              ...(datePreset === 'custom' ? [{ value: 'custom', label: 'Custom range' }] : []),
            ]}
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          From
          <input type="date" aria-label="From date" value={fromDate} max={toDate || undefined}
            onChange={(e) => { setFromDate(e.target.value); setDatePreset('custom'); }}
            className="h-10 rounded-lg border border-border bg-background px-2 text-xs" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
          To
          <input type="date" aria-label="To date" value={toDate} min={fromDate || undefined}
            onChange={(e) => { setToDate(e.target.value); setDatePreset('custom'); }}
            className="h-10 rounded-lg border border-border bg-background px-2 text-xs" />
        </label>
        {anyFilter && (
          <button
            type="button"
            onClick={() => { setSearchInput(''); setRepFilter(''); setClientFilter(''); setProductFilter(''); setStatusFilter(''); setNoFrom(''); setNoTo(''); setSort('invoice_desc'); applyPreset(''); }}
            className="h-10 rounded-lg border border-border px-3 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            Clear filters
          </button>
        )}
      </div>
      <div className="border-b px-4 py-2 text-xs text-muted-foreground">
        <b className="font-mono tabular-nums text-foreground">{filtered.length}</b> of {allInvoices.length} invoice{allInvoices.length === 1 ? '' : 's'}
        {' · '}Value <b className="font-mono tabular-nums font-semibold text-foreground">{formatINR(totalValue)}</b>
        {' · '}Due <b className="font-mono tabular-nums font-semibold text-destructive">{formatINR(totalDue)}</b>
      </div>
      <CardBody className="flex-1 p-0 overflow-auto">
        {filtered.length === 0 ? (
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title={anyFilter ? 'No invoices match these filters' : 'No sales invoices yet'}
            description={anyFilter
              ? 'Widen the date range or clear the filters to see the rest of the ledger.'
              : 'Raise one from a confirmed deal, or add a line item to start the first invoice.'}
            action={!anyFilter ? (
              <Button size="sm" onClick={handleGenerate}>
                <PlusCircle className="h-4 w-4 mr-1" /> New Invoice
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => { setSearchInput(''); setRepFilter(''); setClientFilter(''); setProductFilter(''); setStatusFilter(''); setNoFrom(''); setNoTo(''); applyPreset(''); }}>Clear filters</Button>
            )}
          />
        ) : (
          <table className="w-full text-body-sm text-left">
            <thead className="text-label-caps uppercase text-on-surface-variant bg-muted sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2.5">Invoice #</th>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Customer</th>
                <th className="px-4 py-2.5 text-right">Net Total</th>
                <th className="px-4 py-2.5 text-right">Due</th>
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5">Dispatch</th>
                <th className="px-4 py-2.5">Due Date</th>
                <th className="px-4 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((inv: AccountsSalesInvoice) => (
                <tr key={inv._id} className="hover:bg-muted/50 cursor-pointer transition-colors" onClick={() => handleViewInvoice(inv)}>
                  <td className="px-4 py-2.5 font-semibold text-primary">
                    <div className="flex items-center gap-2">
                      <span>{inv.invoiceNumber || 'DRAFT'}</span>
                      {inv.source === 'deal.won' && (
                        <span className="h-2 w-2 rounded-full bg-info" title="Auto-generated from Deal Won"></span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground font-mono tabular-nums">{new Date(inv.date).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-2.5 font-medium">{(inv.customerId as any)?.name || '—'}</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium text-on-surface">
                    {inv.status === 'cancelled' ? (
                      <div className="flex flex-col items-end">
                        <span className="text-danger-text font-bold">₹0</span>
                        <span className="line-through text-[11px] text-muted-foreground">{formatINR(inv.netTotal || inv.grandTotal)}</span>
                      </div>
                    ) : (
                      formatINR(inv.netTotal || inv.grandTotal)
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                    {inv.status === 'cancelled' ? (
                      <span className="text-danger-text font-semibold">₹0</span>
                    ) : inv.amountDue > 0 ? (
                      <span className="text-destructive">{formatINR(inv.amountDue)}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">{statusBadge(inv.status)}</td>
                  <td className="px-4 py-2.5">{renderDispatchStatus(inv.dispatchStatus)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground font-mono tabular-nums">{new Date(inv.dueDate).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                    {inv.status !== 'draft' && inv.status !== 'cancelled' && inv.amountDue > 0 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => handleOpenPayModal(inv, e)}
                        className="text-xs h-7 font-semibold text-success-text border-success/25 bg-success/10 hover:bg-success/15"
                      >
                        <CreditCard className="h-3 w-3 mr-1" /> Record Payment
                      </Button>
                    ) : (
                      <span className="text-[11px] text-muted-foreground font-medium">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>

      {/* Record Payment Modal */}
      <Modal
        open={!!payModalInvoice}
        onClose={() => setPayModalInvoice(null)}
        title={`Record Payment for ${payModalInvoice?.invoiceNumber || ''}`}
        size="md"
      >
        <form onSubmit={(e) => { e.preventDefault(); recordPaymentMutation.mutate(); }} className="space-y-4 pt-2">
          <div className="rounded-card-sm border border-border bg-muted/30 p-3 text-xs space-y-1">
            <p><strong className="text-muted-foreground">Customer:</strong> {(payModalInvoice?.customerId as any)?.name || '—'}</p>
            <p><strong className="text-muted-foreground">Invoice Total:</strong> <span className="font-mono tabular-nums">{formatINR(payModalInvoice?.netTotal)}</span></p>
            <p><strong className="text-muted-foreground">Current Amount Due:</strong> <span className="font-mono tabular-nums font-semibold text-destructive">{payModalInvoice?.amountDue == null ? '—' : formatINR(payModalInvoice.amountDue)}</span></p>
          </div>

          <div>
            <label className="text-xs font-semibold block mb-1">Payment Date</label>
            <Input type="date" required value={payDate} onChange={e => setPayDate(e.target.value)} />
          </div>

          <div>
            <label className="text-xs font-semibold block mb-1">Payment Amount (₹)</label>
            <Input
              type="number"
              required
              min="0.01"
              step="any"
              value={payAmount}
              onChange={e => setPayAmount(Number(e.target.value))}
            />
          </div>

          {payModalInvoice && payAmount > (payModalInvoice.amountDue || 0) && (
            <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-300 dark:border-emerald-800 p-2.5 text-xs text-emerald-900 dark:text-emerald-200 space-y-1">
              <p className="font-bold flex items-center gap-1">
                <span>💡 Additional / Excess Payment Detected</span>
              </p>
              <div className="flex justify-between text-[11px] font-semibold text-slate-700 dark:text-slate-300">
                <span>Settles Invoice:</span>
                <span className="font-mono">{formatINR(payModalInvoice.amountDue || 0)}</span>
              </div>
              <div className="flex justify-between text-[11px] font-bold text-emerald-700 dark:text-emerald-400">
                <span>Retained as Customer Advance (for next order):</span>
                <span className="font-mono">+{formatINR(payAmount - (payModalInvoice.amountDue || 0))}</span>
              </div>
              <p className="text-[10px] text-muted-foreground pt-0.5">
                The invoice will be fully paid, and the extra balance is automatically credited to Customer Advance to deduct from the next order.
              </p>
            </div>
          )}

          <div>
            <label className="text-xs font-semibold block mb-1">Payment Mode</label>
            <Select
              value={payMode}
              onChange={e => setPayMode(e.target.value)}
              options={[
                { value: 'bank_transfer', label: 'Bank Transfer (NEFT / RTGS / IMPS)' },
                { value: 'cash', label: 'Cash' },
                { value: 'cheque', label: 'Cheque' },
                { value: 'online', label: 'Online Payment Gateway' },
              ]}
            />
          </div>

          <div>
            <label className="text-xs font-semibold block mb-1">Reference / UTR / Cheque #</label>
            <Input placeholder="e.g. UTR193028471" value={payRef} onChange={e => setPayRef(e.target.value)} />
          </div>

          {/* A reference number is a claim; this is the evidence behind it, and
              it is what a dispute or an audit asks for. Uploaded first so the
              receipt is stored even if the form is then abandoned — an orphaned
              file is cheaper than a payment recorded with no proof. */}
          <div>
            <label className="text-xs font-semibold block mb-1">
              Payment proof <span className="font-normal text-muted-foreground">(receipt, screenshot, bank advice — optional)</span>
            </label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf"
              disabled={uploading}
              onChange={async (e) => {
                const chosen = Array.from(e.target.files || []);
                e.target.value = '';
                if (chosen.length === 0) return;
                setUploading(true);
                try {
                  for (const file of chosen) {
                    const form = new FormData();
                    form.append('file', file);
                    form.append('category', 'general');
                    const { data } = await api.post<PaymentAttachment>('/api/files/upload', form);
                    setPayFiles((prev) => [...prev, data]);
                  }
                } catch (err) {
                  toast.error(getApiErrorMessage(err, 'Could not upload that file.'));
                } finally {
                  setUploading(false);
                }
              }}
              className="block w-full text-xs file:mr-3 file:rounded-md file:border file:border-input file:bg-muted file:px-3 file:py-1.5 file:text-xs file:font-semibold"
            />
            {uploading && <p className="mt-1 text-xs text-muted-foreground">Uploading...</p>}
            {payFiles.length > 0 && (
              <ul className="mt-2 space-y-1">
                {payFiles.map((f, i) => (
                  <li key={f.fileUrl} className="flex items-center justify-between gap-2 rounded-md border px-2 py-1 text-xs">
                    <a href={f.fileUrl} target="_blank" rel="noopener noreferrer" className="truncate text-primary hover:underline">
                      {f.originalName || 'Attachment'}
                    </a>
                    <button
                      type="button"
                      className="shrink-0 text-destructive"
                      onClick={() => setPayFiles((prev) => prev.filter((_, n) => n !== i))}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" type="button" onClick={() => { setPayFiles([]); setPayModalInvoice(null); }}>Cancel</Button>
            <Button type="submit" disabled={recordPaymentMutation.isPending} className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              {recordPaymentMutation.isPending ? 'Recording...' : 'Save & Post Receipt'}
            </Button>
          </div>
        </form>
      </Modal>
    </Card>
  );
}
